// `LiveBadge` — the contained "currently live" presence pill: a small
// pulsing dot, a count, and the literal "live" label.
//
// Lives inside the Live In Club bounded context's `ui/atoms/badges/`
// rather than the generic `_shared/design-system/` because the
// concept it represents — Talent currently in the Club — belongs to
// this context's ubiquitous language. See `docs/design-system.md`
// §"Domain-specific UI" for the placement rules; domain primitives
// follow the same authoring conventions (BEM, @layer cascade,
// stories + tests + axe spec) as design-system primitives.
//
// The pulse animation reuses the global `atp-pulse` keyframe defined
// in `theme.css` so the cadence stays consistent with the platform's
// other live indicators and the `prefers-reduced-motion` guard fires
// for it automatically.

import type { HTMLAttributes } from 'react';

import { classlist } from '~/_shared/design-system/lib/classlist';

import './live-badge.css';

interface LiveBadgeProps extends HTMLAttributes<HTMLSpanElement> {
	value: number;
}

// Caps the rendered text at the 999+ convention, matching `CountBadge`.
// The raw numeric value still flows through to the default aria-label.
const formatValue = (value: number): string =>
	value > 999 ? '999+' : String(value);

const LiveBadge = ({
	value,
	id = 'LiveBadge',
	className,
	'aria-label': aria_label,
	...rest
}: LiveBadgeProps) => {
	const resolved_label = aria_label ?? `${value} live`;
	return (
		<span
			{...rest}
			id={id}
			role='status'
			aria-label={resolved_label}
			className={classlist('live-badge', className)}
		>
			<span aria-hidden='true' className='live-badge__dot' />
			<span className='live-badge__value'>{formatValue(value)}</span>
			<span aria-hidden='true' className='live-badge__label'>
				live
			</span>
		</span>
	);
};

export type { LiveBadgeProps };
export { LiveBadge };
